import { Scene } from "@babylonjs/core/scene";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { getAssetManager, type InstantiatedModel } from "../../systems/AssetManager";
import { tagAs } from "../PlaceholderTag";
import placement from "../../../assets/models-src/environment/conservatory_gus_parlor.json";

/** Physical reading alcove remains available for Aura/Biscuits before the auto handoff. */
export class ConservatoryParlorVisuals {
  readonly ready: Promise<void>;
  readonly readingLight: PointLight;
  private lightTargets: AbstractMesh[] = [];
  private readonly root: TransformNode;
  private readonly fallback: AbstractMesh[] = [];
  private readonly fallbackMaterials: PBRMaterial[] = [];
  private model: InstantiatedModel | null = null;
  private casters: AbstractMesh[] = [];
  private disposed = false;

  constructor(private readonly scene: Scene, x: number, private readonly shadows: ShadowGenerator) {
    this.root = new TransformNode("conservatoryParlor", scene); this.root.position.x = x;
    const leather = this.material("leather", new Color3(.12, .055, .025));
    const wood = this.material("wood", new Color3(.16, .085, .04));
    const wool = this.material("wool", new Color3(.16, .045, .08));
    const box = (name: string, p: readonly number[], size: readonly number[], mat: PBRMaterial) => {
      const m = MeshBuilder.CreateBox("parlorFallback_" + name, { width: size[0], height: size[1], depth: size[2] }, scene);
      m.parent = this.root; m.position.set(p[0], p[1], p[2]); m.material = mat;
      m.isPickable = false; m.receiveShadows = true; tagAs(m, "placeholder"); this.fallback.push(m);
    };
    for (const xx of [-.90, .90]) for (const zz of [-.98, -2.4]) box("leg", [xx, .25, zz], [.13, .50, .13], wood);
    box("seat", [.10, placement.seatTopY - .09, -1.7], [2.12, .18, 1.52], leather);
    box("back", [1.10, 1.55, -1.7], [.23, 2.02, 1.78], leather);
    for (const zz of [-.79, -2.61]) box("arm", [0, 1.12, zz], [1.8, .20, .20], leather);
    box("throw", [-.10, 1.16, -.79], [1.10, .09, .46], wool);
    box("throwFold", [-.10, .70, -.56], [1.10, .85, .035], wool);
    box("screen", [0, 1.65, -3.25], [4.8, 3.3, .06], wool);
    box("threshold", [3, .008, 0], [2.5, .016, 1.15], wood);
    this.readingLight = new PointLight("conservatoryParlor_readingLamp",
      new Vector3(x + placement.lamp.x, placement.lamp.y, placement.lamp.z), scene);
    this.readingLight.diffuse.set(.85, .65, .36); this.readingLight.specular = Color3.Black();
    this.readingLight.intensity = 3.5; this.readingLight.range = 5;
    this.setFurnitureLightTargets(this.fallback);
    this.registerCasters(this.fallback); this.ready = this.load();
  }
  private material(name: string, color: Color3): PBRMaterial {
    const m = new PBRMaterial("parlorFallback_" + name, this.scene); m.albedoColor = color; m.roughness = .94; m.metallic = 0;
    this.fallbackMaterials.push(m); return m;
  }
  private async load(): Promise<void> {
    try {
      const model = await getAssetManager().instantiateModel(this.scene,
        "/assets/models/environment/conservatory_gus_parlor.glb", undefined, { doNotInstantiate: true });
      if (this.disposed || this.scene.isDisposed) { model.dispose(); return; }
      const names = ["ParlorSeat_cushion", "Parlor_woven_throw", "Parlor_leather_assembly"];
      if (names.some(name => !model.meshes.some(m => m.name.endsWith(name) && m.getTotalVertices() > 0))) {
        model.dispose(); throw new Error("Parlor is missing its seat, throw or upholstered chair");
      }
      for (const node of model.rootNodes) { node.parent = this.root; tagAs(node, "final"); }
      for (const mesh of model.meshes) { mesh.isPickable = false; mesh.receiveShadows = true; tagAs(mesh, "final"); }
      this.clearCasters(); this.registerCasters(model.meshes);
      this.model = model; this.setFurnitureLightTargets(model.meshes);
      for (const mesh of this.fallback) mesh.isVisible = false;
    } catch (error) {
      if (!this.disposed) console.warn("[ConservatoryParlorVisuals] Keeping supported furniture fallback:", error);
    }
  }
  private setFurnitureLightTargets(meshes: readonly AbstractMesh[]): void {
    const otherOwners = this.readingLight.includedOnlyMeshes.filter(mesh => !this.lightTargets.includes(mesh));
    this.lightTargets = meshes.filter(mesh => mesh.getTotalVertices() > 0);
    this.readingLight.includedOnlyMeshes = [...otherOwners, ...this.lightTargets];
  }
  private registerCasters(meshes: readonly AbstractMesh[]): void {
    for (const mesh of meshes) if (mesh.getTotalVertices() > 0) {
      this.shadows.addShadowCaster(mesh, false); this.casters.push(mesh);
    }
  }
  private clearCasters(): void {
    for (const mesh of this.casters) this.shadows.removeShadowCaster(mesh, false);
    this.casters = [];
  }
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true; this.readingLight.dispose(); this.lightTargets = []; this.clearCasters(); this.model?.dispose(); this.model = null;
    this.root.dispose(); this.fallbackMaterials.forEach(m => m.dispose());
  }
}
